import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useReveal } from '../hooks/useReveal';
import './DivisionPage.css';

export interface Feature {
  titleKey: string;
  descKey: string;
}

export interface Shot {
  src: string;
  captionKey: string;
}

export interface Spec {
  labelKey: string;
  valueKey: string;
}

interface DivisionPageProps {
  accent: string;
  icon: LucideIcon;
  nameKey: string;
  badgeKey: string;
  titleKey: string;
  leadKey: string;
  pitchKey: string;
  features: Feature[];
  shots?: Shot[];
  specs?: Spec[];
  link?: { href: string; titleKey: string; descKey: string };
  underTitle?: ReactNode;
  children?: ReactNode;
}

export function DivisionPage({
  accent,
  icon: Icon,
  nameKey,
  badgeKey,
  titleKey,
  leadKey,
  pitchKey,
  features,
  shots,
  specs,
  link,
  underTitle,
  children,
}: DivisionPageProps) {
  const { t, language } = useI18n();
  useReveal([nameKey, language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nameKey]);

  return (
    <div className={`division-page ${accent}`}>
      <header className="dp-hero">
        <span className="dp-hero-glow" aria-hidden="true" />
        <div className="dp-hero-inner">
          <Link to="/" className="dp-back reveal">
            <ArrowLeft size={16} />
            <span>{t('common.backHome')}</span>
          </Link>

          <div className="dp-hero-head reveal">
            <span className="dp-icon"><Icon size={28} /></span>
            <div>
              <div className="dp-name">{t(nameKey)}</div>
              <span className="tile-badge">{t(badgeKey)}</span>
            </div>
          </div>

          <h1 className="dp-title reveal">{t(titleKey)}</h1>
          <p className="dp-lead reveal">{t(leadKey)}</p>

          {underTitle && (
            <div className="dp-under-title reveal">
              {underTitle}
            </div>
          )}

          {specs && (
            <div className="dp-specs reveal">
              {specs.map((s) => (
                <div className="dp-spec" key={s.labelKey}>
                  <span>{t(s.labelKey)}</span>
                  <strong>{t(s.valueKey)}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      <section className="section dp-section">
        <div className="dp-pitch reveal">
          <div className="section-eyebrow">{t('common.whatWeBuild')}</div>
          <p>{t(pitchKey)}</p>
        </div>

        <div className="dp-features">
          {features.map((f, i) => (
            <article className="dp-feature reveal" key={f.titleKey} style={{ transitionDelay: `${i * 60}ms` }}>
              <span className="dp-feature-index">{String(i + 1).padStart(2, '0')}</span>
              <h3>{t(f.titleKey)}</h3>
              <p>{t(f.descKey)}</p>
            </article>
          ))}
        </div>
      </section>

      {shots && shots.length > 0 && (
        <section className="section dp-section dp-gallery-section">
          <div className="section-eyebrow reveal">{t('common.gallery')}</div>
          <div className={`dp-gallery ${shots.length === 2 ? 'is-two' : ''}`}>
            {shots.map((s, i) => (
              <figure className="dp-shot reveal" key={s.src} style={{ transitionDelay: `${i * 70}ms` }}>
                <img src={s.src} alt={t(s.captionKey)} loading="lazy" />
                <figcaption>{t(s.captionKey)}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {link && (
        <section className="section dp-section">
          <a className="dp-link-card reveal" href={link.href} target="_blank" rel="noreferrer">
            <div>
              <h3>{t(link.titleKey)}</h3>
              <p>{t(link.descKey)}</p>
              <span className="dp-link-url">{link.href.replace('https://', '')}</span>
            </div>
            <span className="dp-link-icon"><ExternalLink size={22} /></span>
          </a>
        </section>
      )}

      {children}
    </div>
  );
}
